/**
 * Row 1 of the day sheet: the date is the axis.
 *
 * The direction contract for this surface is a page torn from a desk calendar, so
 * the `<h1>` is **the day**, not the word "Overview" — the rail already says which
 * screen this is, with `aria-current`, and a heading that repeats it spends the one
 * 26px line on a label instead of on the only thing that changes.
 *
 * ── The flip is a URL change, not component state ───────────────────────────
 *
 * `?date=` joins `?window=` and `?department_id=` in the query string for the reason
 * `FilterBar`'s header gives: a drill-down built from the search params cannot carry
 * a different day than the sheet it was clicked from, and a sheet pasted into a chat
 * shows the reader the day the sender was looking at. "Next day" stops at today
 * because there is no rollup for tomorrow — a disabled control here asserts nothing
 * about permission, only that the future has not happened.
 *
 * ── Two stamps, never one ───────────────────────────────────────────────────
 *
 * `PRODUCT.md` §4's ninth row: agent sync and achau sync fail independently and are
 * fixed by different people, so `LastSync` prints both and this screen hands it the
 * two instants it has actually read.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { addDays, formatDay } from '../../i18n/datetime';
import { ScreenHeading } from '../../shell/ScreenHeading';
import { Icon } from '../../states/icons';
import { LastSync } from '../../states/LastSync';

// Re-exported so `FilterBar.tsx`'s existing `import { formatDay } from
// './DaySheetHead'` keeps working — `formatDay` itself now lives in
// `i18n/datetime.ts` beside `formatMonth`, the one place every screen that prints a
// date as text reads from.
export { formatDay };

export interface DaySheetHeadProps {
  /** The day being read, `YYYY-MM-DD`. */
  readonly date: string;
  /** Today in the company zone — the ceiling for the forward flip. */
  readonly today: string;
  readonly timezone: string;
  /** Newest successful read of this screen's own regions. */
  readonly agentSyncAt: string | null;
  /** Newest `cached_at` seen in the task probe. */
  readonly achauSyncAt: string | null;
  readonly refreshing: boolean;
  readonly onDate: (next: string) => void;
}

export function DaySheetHead({
  date,
  today,
  timezone,
  agentSyncAt,
  achauSyncAt,
  refreshing,
  onDate,
}: DaySheetHeadProps): ReactNode {
  const { t, locale } = useI18n();
  const isToday = date === today;

  return (
    <header className="wp-daysheet__head" data-testid="k4-day">
      <div className="wp-daysheet__date">
        <ScreenHeading>
          {formatDay(date, locale, { day: 'numeric', month: 'long', year: 'numeric' })}
        </ScreenHeading>
        <p className="wp-daysheet__weekday" data-testid="k4-day-weekday">
          {formatDay(date, locale, { weekday: 'long' })}
          {isToday ? ` · ${t('overview.day.isToday')}` : ''}
        </p>
      </div>

      <div className="wp-daysheet__flip">
        <button
          type="button"
          className="wp-button wp-button--sm"
          data-testid="k4-day-previous"
          onClick={() => {
            onDate(addDays(date, -1));
          }}
        >
          <Icon name="arrow-left-to-line" />
          {t('overview.day.previous')}
        </button>
        <button
          type="button"
          className="wp-button wp-button--sm"
          data-testid="k4-day-next"
          disabled={isToday}
          onClick={() => {
            onDate(addDays(date, 1));
          }}
        >
          {t('overview.day.next')}
          <Icon name="arrow-right-to-line" />
        </button>
        {isToday ? null : (
          <button
            type="button"
            className="wp-button wp-button--sm wp-button--quiet"
            data-testid="k4-day-today"
            onClick={() => {
              onDate(today);
            }}
          >
            {t('overview.day.backToToday')}
          </button>
        )}
      </div>

      <div className="wp-daysheet__stamps" aria-live="polite" data-testid="k4-updated-at">
        <LastSync
          agentSyncAt={agentSyncAt}
          achauSyncAt={achauSyncAt}
          timezone={timezone}
          testId="k4-lastsync"
        />
        {refreshing ? (
          <p className="wp-small wp-muted" data-testid="k4-refreshing">
            {t('overview.updating')}
          </p>
        ) : null}
      </div>
    </header>
  );
}
